/*
 * Rebuy widget readiness
 * -----------------------------------------------------------------------------
 *
 * Rebuy dispatches `rebuy.ready` on the document once per widget it renders. Its
 * script is an app embed, so that can land either side of the theme's sections
 * loading — a section that only subscribed on load would silently miss a widget
 * that was already ready by then. Every widget seen is kept here and replayed to
 * late subscribers, which makes the ordering a non-issue.
 *
 * The listener starts when this module is evaluated, and the theme bundle
 * (src/entrypoints/scripts/theme.js) imports it on every page: carousel sections
 * mount only in view, while Rebuy can render their widget earlier (first
 * interaction, or a widget nearing the viewport), so subscribing on mount alone
 * missed those and left the skeleton up until its timeout.
 */

const readyWidgets = new Map();
const subscribers = new Set();

function onRebuyReady(event) {
    const widget = event?.detail?.widget;

    // Rebuy uses the same event for bundles, the smart cart and other modules.
    if (!widget || widget.moduleType !== 'widget') {
        return;
    }

    readyWidgets.set(widget.id, widget);
    subscribers.forEach((subscriber) => subscriber(widget));
}

document.addEventListener('rebuy.ready', onRebuyReady);

/**
 * Calls `subscriber` for every carousel widget Rebuy has rendered — both the
 * ones already ready and any that follow.
 *
 * @param {(widget: object) => void} subscriber
 * @returns {() => void} Unsubscribe, for a section's onUnload.
 */
export default function subscribeToRebuyWidgets(subscriber) {
    subscribers.add(subscriber);
    readyWidgets.forEach((widget) => subscriber(widget));

    return () => subscribers.delete(subscriber);
}
